import { useEffect, useRef, useState } from 'react'
import { Button } from '@mui/material'
import { styled } from '@mui/system'
import { Column, GridApi } from 'ag-grid-community'
import {
  addEventListenerForLastRow,
  dateTimeValueParser,
  dateValueParser,
} from '../../../..'
import DatePicker from 'react-datepicker'
import {
  FunctionProperty,
  PropertyType,
} from '../../../../../../../../lib/commons/appFunction'
import InputBase from '@mui/material/InputBase'
import moment from 'moment'
import {
  DISPLAY_DATE_FORMAT,
  DISPLAY_DATETIME_FORMAT,
} from '../../../../../../../../utils/date'
import {
  DateVO,
  dateVoService,
} from '../../../../../../../../domain/value-object/DateVO'

// Styles
const RootDiv = styled('div')({
  width: '100%',
  height: '100%',
  padding: 0,
  display: 'flex',
  alignItems: 'center',
  border: 'none',
  backgroundColor: 'inherit',
})
const StyledDatePicker = styled(DatePicker)({
  width: '100%',
  height: '100%',
  border: 'none',
  padding: 0,
  display: 'flex',
  backgroundColor: 'inherit',
  margin: 0,
})

interface Props {
  value: number
  api: GridApi
  eventKey: string | null
  rowIndex: number
  column: Column

  uiMeta: FunctionProperty
  onValueChange: (value: any) => void

  getInitialValueOnCalendar?: (params: Props) => DateVO | undefined
}

const INPUT_DATE_FORMAT = 'yyyy/MM/dd'

const parseInitialValue = (rawValue: number): number | undefined => {
  let defaultVal = rawValue ? moment(rawValue) : undefined
  if (
    defaultVal &&
    !defaultVal.isValid() &&
    typeof rawValue === 'string' &&
    !isNaN(rawValue as any)
  ) {
    defaultVal = moment(parseInt(rawValue as any, 10))
  }
  return defaultVal?.toDate().getTime()
}

const DateCellEditor = (props: Props) => {
  const showTime = props.uiMeta.propertyType === PropertyType.DateTime
  const displayFormat = showTime ? DISPLAY_DATETIME_FORMAT : DISPLAY_DATE_FORMAT
  const valueParser = showTime ? dateTimeValueParser : dateValueParser

  const [value, setValue] = useState<number | undefined>(() =>
    parseInitialValue(props.value)
  )
  const [initialInputValue, setInitialInputValue] = useState<
    string | undefined
  >(() => {
    if (props.eventKey === 'Delete' || props.eventKey === 'Backspace') return ''
    if (!props.eventKey || props.eventKey.length > 1) {
      return value ? moment(value).format(displayFormat) : ''
    }
    return props.eventKey
  })
  const [open, setOpen] = useState(false)

  const inputRef = useRef<HTMLInputElement>(null)
  const changedByInputRef = useRef(false)
  const tmpValueRef = useRef<number | undefined>(undefined)
  const valueRef = useRef(value)

  const updateValue = (newValue: number | undefined) => {
    valueRef.current = newValue
    setValue(newValue)
  }

  const notifyValueChange = (val?: number) => {
    const formatted = val ? moment(val).format(displayFormat) : undefined
    props.onValueChange(formatted)
  }

  const onFinishEdit = () => {
    const focusedCell = props.api.getFocusedCell()
    props.api.clearFocusedCell()
    props.api.setFocusedCell(focusedCell!.rowIndex, focusedCell!.column)
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      addEventListenerForLastRow(inputRef.current, props)
    }
    if (props.eventKey === 'Delete' || props.eventKey === 'Backspace') {
      updateValue(undefined)
      notifyValueChange(undefined)
      onFinishEdit()
    }
    // initialInputValue は useState 初期化で設定済み。ここではクリアのみ
    setInitialInputValue(undefined)
    setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行。セルエディタの初期化処理
  }, [])

  const handleChange = (date: Date) => {
    if (!changedByInputRef.current) {
      const timeValue = date ? date.getTime() : undefined
      updateValue(timeValue)
      notifyValueChange(timeValue)
    }
  }

  const handleSelect = (date: Date) => {
    // Not set value if fired event while edit input value
    if (!changedByInputRef.current) {
      const timeValue = date ? date.getTime() : undefined
      updateValue(timeValue)
      notifyValueChange(timeValue)
    } else {
      changedByInputRef.current = false
    }
  }

  const handleChangeCapture = () => {
    if (inputRef.current) {
      const parsed = valueParser(inputRef.current.value, props.uiMeta)
      if (parsed) {
        tmpValueRef.current = moment(parsed.replace(/\//g, '-'))
          .toDate()
          .getTime()
        changedByInputRef.current = true
        if (
          !isNaN(tmpValueRef.current) &&
          tmpValueRef.current !== valueRef.current
        ) {
          notifyValueChange(tmpValueRef.current)
          updateValue(tmpValueRef.current)
          // changedByInput は onSelect の else ブランチでリセットされる
        }
      }
    }
  }

  const handleButtonPressed = () => {
    setOpen(false)
    onFinishEdit()
  }

  const getContainer = ({
    className,
    children,
  }: {
    className: string
    children: React.ReactNode
  }) => {
    if (!showTime) {
      return <div className={className}>{children}</div>
    }
    return (
      <div className={className}>
        {children}
        <Button
          style={{
            display: 'block',
            width: '100%',
            borderTop: '1px solid #aeaeae',
            borderRadius: 0,
            backgroundColor: '#f0f0f0',
          }}
          onClick={handleButtonPressed}
        >
          OK
        </Button>
      </div>
    )
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const nextRowIndex =
        props.api.getLastDisplayedRowIndex() !== props.rowIndex
          ? props.rowIndex + 1
          : props.rowIndex
      props.api.stopEditing()
      props.api.clearCellSelection()
      props.api.clearFocusedCell()
      props.api.setFocusedCell(nextRowIndex, props.column)
    }
  }

  const initialValueOnCalendar = props.getInitialValueOnCalendar
    ? props.getInitialValueOnCalendar(props)
    : undefined

  return (
    <RootDiv>
      <StyledDatePicker
        open={open}
        selected={
          value ||
          (initialValueOnCalendar
            ? dateVoService.toNumberValue(initialValueOnCalendar)
            : undefined)
        }
        dateFormat={INPUT_DATE_FORMAT}
        onChange={handleChange}
        onSelect={handleSelect}
        onCalendarClose={onFinishEdit}
        onKeyDown={handleKeyDown}
        showTimeInput={showTime}
        disabledKeyboardNavigation={true}
        shouldCloseOnSelect={!showTime}
        customInput={
          <InputBase
            inputRef={inputRef}
            autoFocus={true}
            inputProps={{
              value: initialInputValue,
            }}
            onChangeCapture={handleChangeCapture}
          />
        }
        calendarContainer={getContainer}
        fixedHeight={true}
      />
    </RootDiv>
  )
}

export default DateCellEditor
